// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awBorderGrey } from 'Utils/themes';

// The dashboard's ground; ProjectsSubheader's styles (the redesign's controls band, PR 2 of
// AUT-6502) paint it too.
export const DASHBOARD_BACKGROUND_COLOR = '#fafafa';

const styles = (theme: any, showMapView: boolean) => {
  const mapViewHeadingSpacing = showMapView && {
    width: '80%',
    margin: '0 auto',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  };

  return {
    contentWrapperGrid: {
      position: 'relative',
      flex: 1,
      backgroundColor: DASHBOARD_BACKGROUND_COLOR,
    },
    contentWrapperMap: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      minWidth: '100%',
    },
    // Map view swaps the content area for the map: the toolbar keeps its row, the map takes the
    // rest, and its height comes from the flex column rather than a viewport calculation.
    mapviewContent: {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
      flexDirection: 'column',
    },
    // The rail and the map share the map view's full height (Figma: no top toolbar here).
    mapviewBody: {
      display: 'flex',
      flexDirection: 'row',
      flex: '1 1 auto',
      minHeight: 0,
    },
    // Figma's exit affordance, floating on the map: white, hairline border, 4px radius.
    mapCloseButton: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: 3,
      cursor: 'pointer',
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${awBorderGrey}`,
      borderRadius: '4px',
      height: 38,
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontSize: '14px',
      letterSpacing: '0.7px',
      padding: '10px 12px',
    },
    mapArea: {
      position: 'relative',
      flex: '1 1 auto',
      minHeight: 0,
    },
    mapEmptyState: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(4),
      borderRadius: '8px',
      boxShadow: theme.shadows[3],
      zIndex: 1,
    },
    // Projects excluded for having no resolvable location, surfaced rather than silently dropped.
    mapMissingLocation: {
      position: 'absolute',
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      backgroundColor: theme.palette.common.white,
      color: theme.palette.darkGrey,
      padding: theme.spacing(1, 2),
      borderRadius: '16px',
      boxShadow: theme.shadows[2],
      fontSize: '13px',
      zIndex: 1,
    },
    gridList: {
      paddingBottom: theme.spacing(3),
      display: 'grid',
      // Three columns at the design's width, fewer as the container narrows, rather than
      // three columns squeezed to any width. 320px is the narrowest a card stays readable
      // at; at the 1200px content width this still resolves to the Figma's three.
      // The explicit minimum also keeps a card's unbreakable rows (its name and REF line
      // never wrap) from widening every track: a bare 1fr is minmax(auto, 1fr), and one long
      // name would push the whole grid past the page.
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gridGap: 30,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(5),
    },
    // The design is drawn at 1200px; below that the content follows the viewport instead of
    // overflowing it.
    mapList: {
      width: '80%',
      margin: 'auto',
      display: 'flex',
      flexDirection: 'column',
      ...(!showMapView && {
        position: 'relative',
        top: '95px',
        zIndex: 1,
        height: '100vh' }),
    },
    mapListWrapper: {
      margin: 0,
      marginTop: '7rem',
      height: 'calc(100vh - 7rem)',
    },
    gridListWrapper: {
      width: '100%',
      maxWidth: '1200px',
      margin: '100px auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      boxSizing: 'border-box',
    },
    dashboardContent: {
      display: 'flex',
      flex: '1 1 auto',
      position: 'relative',
      minHeight: 0,
      flexDirection: 'row',
    },
    mapviewLayoutLeft: {
      flexGrow: 1,
      width: '50%',
      flex: 1,
      overflow: 'auto',
      margin: 0,
      position: 'relative',
      flexDirection: 'column',
    },
    gridItemWrapper: {
      overflow: 'inherit',
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
    },
    // The list view: rows stacked full-width, the window's spacers as siblings around it.
    rowList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(5),
      paddingBottom: theme.spacing(3),
    },
    // The empty projects section keeps the content column's width, controls included.
    emptyProjects: {
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    noProjects: {
      width: '100%',
      textAlign: 'center',
      marginTop: '20vh',
    },
    retryButton: {
      marginTop: theme.spacing(3),
    },
    resultsCaption: {
      fontSize: '14px',
      fontWeight: 400,
      color: theme.palette.darkGrey,
      ...mapViewHeadingSpacing,
    },
    listTitle: {
      fontSize: '18px',
      fontWeight: 600,
      ...mapViewHeadingSpacing,
    },
  };
};

export default styles;
